import React, { useState, useRef, useEffect } from "react";
import { apiErrorText } from '../lib/apiError';
import useSWR from "swr";
import apiV2, { orgApi, getOrgId } from "../utils/apiV2";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  AlertCircle, ArrowRight, Phone, Mail, Sparkles, Check,
  Loader2, Users, Anchor, ChevronDown, User
} from "lucide-react";
import TabPageLayout from './TabPageLayout';
import { tzAbbr } from '../utils/locale';
import EmptyState from './EmptyState';
import BookingCard from './BookingCard';
import EditBookingModal from './EditBookingModal';
import { MultiChipTabs } from './ui/chip-tabs';
import SourceFilter from './SourceFilter';
import { useSourceFilter } from '../utils/bookingSource';

// Tiny inline mini-calendar — no react-day-picker dependency
const MiniCalendar = ({ selected, onSelect }) => {
  const [viewDate, setViewDate] = useState(selected || new Date());
  
  // Sync view month when selected date changes externally
  const selectedYear = selected?.getFullYear();
  const selectedMonth = selected?.getMonth();
  useEffect(() => {
    if (selected) setViewDate(new Date(selected.getFullYear(), selected.getMonth(), 1));
  }, [selectedYear, selectedMonth]);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date(); today.setHours(0,0,0,0);
  const selStr = selected ? `${selected.getFullYear()}-${selected.getMonth()}-${selected.getDate()}` : '';
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="p-3 w-[280px]">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-1 rounded hover:bg-gray-100">
          <ChevronLeft className="w-4 h-4 text-gray-500" />
        </button>
        <span className="text-sm font-semibold text-gray-900">
          {viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-1 rounded hover:bg-gray-100">
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
          <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const date = new Date(year, month, d);
          date.setHours(0,0,0,0);
          const isToday = date.getTime() === today.getTime();
          const isSelected = selStr === `${year}-${month}-${d}`;
          return (
            <button
              key={i}
              onClick={() => onSelect(date)}
              className={`w-8 h-8 mx-auto rounded-full text-sm transition-colors ${
                isSelected ? 'bg-blue-600 text-white font-semibold' :
                isToday ? 'bg-blue-50 text-blue-600 font-semibold' :
                'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Fetch daily schedule
const fetchDailySchedule = async (dateStr, paymentFilter = 'paid') => {
  const params = new URLSearchParams();
  if (dateStr) params.set('date_str', dateStr);
  if (paymentFilter) params.set('payment_filter', paymentFilter);
  const response = await apiV2.get(`/schedule/daily?${params.toString()}`);
  return response.data;
};

// Format date for API (YYYY-MM-DD) — uses LOCAL date, not UTC
const formatDateForApi = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Get today's date (no time component)
const getToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// Extract hour from time string (e.g., "9:30 AM" -> "9 AM")
const getHourSlot = (timeStr) => {
  if (!timeStr) return "Unknown";
  const match = timeStr.match(/^(\d+):/);
  if (!match) return timeStr;
  const hour = parseInt(match[1]);
  const isPM = timeStr.toUpperCase().includes('PM');
  return `${hour} ${isPM ? 'PM' : 'AM'}`;
};

// Group bookings by hour slot
const groupByHour = (bookings) => {
  const groups = {};
  bookings.forEach(booking => {
    const slot = getHourSlot(booking.start_time);
    if (!groups[slot]) {
      groups[slot] = [];
    }
    groups[slot].push(booking);
  });
  return groups;
};

// Rotating colors for time slot left borders
const SLOT_COLORS = [
  '#3B82F6', // blue
  '#8B5CF6', // purple
  '#14B8A6', // teal
  '#F97316', // orange
  '#EC4899', // pink
  '#10B981', // emerald
  '#6366F1', // indigo
  '#F59E0B', // amber
];

const ScheduleTab = () => {

  const [currentDate, setCurrentDate] = useState(getToday());
  const [paymentFilter, setPaymentFilter] = useState(['paid', 'deposit', 'partial', 'unpaid']); // multi-select, mirrors Manifest defaults
  const [sourceKeys, toggleSource, sourceFilterFn] = useSourceFilter(); // channel show/hide (front-end only)
  const [contactModal, setContactModal] = useState({ open: false, type: null, value: null, name: null, booking: null });
  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const datePickerRef = useRef(null);

  // Suggest Schedule state
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyTeam, setNotifyTeam] = useState(true);
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  // Compute date display locally — never shows "Loading..."
  const formatDateDisplay = (date) => {
    const today = getToday();
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const formatted = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    if (formatDateForApi(date) === formatDateForApi(today)) return `Today, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    if (formatDateForApi(date) === formatDateForApi(tomorrow)) return `Tomorrow, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    if (formatDateForApi(date) === formatDateForApi(yesterday)) return `Yesterday, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    return formatted;
  };

  const dateStr = formatDateForApi(currentDate);
  const paymentFilterKey = paymentFilter.join(',');

  const { data, error, isLoading, mutate } = useSWR(
    ['daily-schedule', dateStr, paymentFilterKey],
    () => fetchDailySchedule(dateStr, paymentFilterKey),
    { 
      revalidateOnFocus: true,
      dedupingInterval: 30000
    }
  );

  const rawBookings = data?.bookings || [];
  // Never surface cancelled bookings unless the Cancelled chip is on
  const bookings = (paymentFilter.includes('cancelled')
    ? rawBookings
    : rawBookings.filter(b => (b.status || '').toLowerCase() !== 'cancelled')
  ).filter(sourceFilterFn);
  const checkoutBookings = data?.checkout_bookings || [];
  const nextBookingDate = data?.next_booking_date;
  const groupedBookings = groupByHour(bookings);
  const hourSlots = Object.keys(groupedBookings);

  // Navigate days
  const goToPreviousDay = () => {
    const prev = new Date(currentDate);
    prev.setDate(prev.getDate() - 1);
    setCurrentDate(prev);
  };

  const goToNextDay = () => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + 1);
    setCurrentDate(next);
  };

  const goToToday = () => {
    setCurrentDate(getToday());
  };

  const jumpToNextBooking = () => {
    if (nextBookingDate) {
      // nextBookingDate is "YYYY-MM-DD" from the API. Append T00:00:00 (no Z) so JS
      // parses as local midnight, not UTC midnight (which shifts the date in US timezones).
      setCurrentDate(new Date(nextBookingDate + 'T00:00:00'));
    }
  };

  // Check if viewing today
  const isToday = formatDateForApi(currentDate) === formatDateForApi(getToday());

  // Check if an hour slot has passed (only relevant for today)
  const isHourPassed = (hourSlot) => {
    if (!isToday) return false;
    try {
      const match = hourSlot.match(/^(\d+)\s*(AM|PM)$/i);
      if (!match) return false;
      let hour = parseInt(match[1]);
      const isPM = match[2].toUpperCase() === 'PM';
      if (isPM && hour !== 12) hour += 12;
      if (!isPM && hour === 12) hour = 0;
      return new Date().getHours() > hour;
    } catch {
      return false;
    }
  };

  // Handle contact click - now includes booking for resend feature
  const handleContact = ({ type, value, name, booking }) => {
    setContactModal({ open: true, type, value, name, booking });
  };

  // Handle resending confirmation
  const handleResendConfirmation = async (resendType) => {
    if (!contactModal.booking) {
      console.error('No booking in modal');
      toast.error('No booking selected');
      return;
    }
    
    const orgId = contactModal.booking.organization_id || contactModal.booking.organizationId;
    const bookingId = contactModal.booking.id;
    
    if (!orgId) {
      console.error('No organization_id on booking');
      toast.error('Booking missing organization ID');
      return;
    }
    
    setResendingConfirmation(true);
    try {
      const url = `/organizations/${orgId}/bookings/${bookingId}/resend-confirmation`;
      
      const response = await apiV2.post(url, { type: resendType });
      
      if (response.data.success) {
        toast.success(`Confirmation ${resendType === 'email' ? 'email' : 'SMS'} sent!`);
        setContactModal({ open: false, type: null, value: null, name: null, booking: null });
      } else {
        toast.error(response.data.message || 'Failed to resend');
      }
    } catch (err) {
      console.error('Resend error:', err);
      toast.error(apiErrorText(err, 'Failed to resend confirmation'));
    } finally {
      setResendingConfirmation(false);
    }
  };

  // Handle edit booking — opens the shared EditBookingModal inline (no tab jump).
  const [editing, setEditing] = useState(null);
  const [editCtx, setEditCtx] = useState({ assets: [], teamMembers: [], roles: [], services: [] });
  const [editLoading, setEditLoading] = useState(false);
  const handleEdit = async (booking) => {
    if (editLoading) return;
    setEditLoading(true);
    try {
      const [bRes, aRes, tRes, rRes, sRes] = await Promise.all([
        orgApi.getBooking(booking.id),
        orgApi.getAssets(),
        orgApi.getTeamMembers(),
        orgApi.getRoles(),
        orgApi.getServices(),
      ]);
      setEditCtx({
        assets: aRes?.data?.assets || aRes?.data || [],
        teamMembers: tRes?.data?.teamMembers || tRes?.data || [],
        roles: rRes?.data?.roles || rRes?.data || [],
        services: sRes?.data?.services || sRes?.data || [],
      });
      const d = bRes?.data || {};
      setEditing({
        ...d,
        assetId: d.assetId ?? d.asset?.id ?? null,
        serviceId: d.serviceId ?? d.service?.id ?? null,
        serviceName: d.serviceName ?? d.service?.name ?? null,
      });
    } catch (e) {
      toast.error('Could not open the booking editor');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Suggest Schedule ────────────────────────────────────────────────────────
  const handleSuggestSchedule = async () => {
    setSuggesting(true);
    setSuggestOpen(true);
    try {
      // Fetch all required data in parallel
      const [tmRes, assetRes, svcRes] = await Promise.all([
        orgApi.getTeamMembers(),
        orgApi.getAssets(),
        orgApi.getServices(),
      ]);
      const teamMembers = tmRes.data?.teamMembers || tmRes.data || [];
      const assets      = assetRes.data?.assets    || assetRes.data || [];
      const services    = svcRes.data?.services    || svcRes.data  || [];

      const toMins = (t) => {
        if (!t) return 0;
        const [h, m] = String(t).split(':').map(Number);
        return h * 60 + (m || 0);
      };

      // ── Timelines for collision detection ──────────────────────────────────
      // groupKey = null means exclusive; same groupKey = shared per-ticket slot
      const assetTimeline = {}; // { assetId: [{start, end, groupKey}] }
      const teamTimeline  = {}; // { tmId:    [{start, end, groupKey}] }

      const slotFree = (timeline, id, start, end, groupKey) => {
        return !(timeline[id] || []).some(s =>
          s.start < end && s.end > start && s.groupKey !== groupKey
        );
      };
      const markUsed = (timeline, id, start, end, groupKey) => {
        if (!timeline[id]) timeline[id] = [];
        // Only add if not already marked for this groupKey
        if (!timeline[id].some(s => s.groupKey === groupKey && s.start === start))
          timeline[id].push({ start, end, groupKey });
      };

      // ── Group per-ticket bookings by (serviceId + startTime) ───────────────
      // Bookings sharing the same service + time slot fill the same boat together
      const perTicketGroups = {}; // key → { bookings[], service, startMins, endMins }
      const exclusiveList   = []; // each booking is its own unit

      bookings.forEach(b => {
        const svc = services.find(s => s.id === (b.serviceId || b.service_id));
        const mode = svc?.bookingMode || svc?.booking_mode || b.bookingMode || b.booking_mode || 'exclusive';
        const isShared = mode === 'per_ticket';
        if (isShared) {
          const key = `pt_${b.serviceId || b.service_id}_${b.start_time}`;
          if (!perTicketGroups[key]) {
            perTicketGroups[key] = { bookings: [], service: svc, key,
              startMins: toMins(b.start_time), endMins: toMins(b.start_time) + (b.duration_minutes || 60) };
          }
          perTicketGroups[key].bookings.push(b);
        } else {
          exclusiveList.push({ booking: b, service: svc, key: null });
        }
      });

      // ── Build allocation units sorted by start time ─────────────────────────
      const units = [
        // Per-ticket groups
        ...Object.values(perTicketGroups).map(g => ({
          bookings:   g.bookings,
          service:    g.service,
          groupKey:   g.key,
          isShared:   true,
          startMins:  g.startMins,
          endMins:    g.endMins,
          totalParty: g.bookings.reduce((n, b) => n + (b.party_size || b.partySize || 1), 0),
        })),
        // Exclusive bookings
        ...exclusiveList.map(({ booking: b, service: svc }) => ({
          bookings:   [b],
          service:    svc,
          groupKey:   `ex_${b.id}`,
          isShared:   false,
          startMins:  toMins(b.start_time),
          endMins:    toMins(b.start_time) + (b.duration_minutes || 60),
          totalParty: b.party_size || b.partySize || 1,
        })),
      ].sort((a, b) => a.startMins - b.startMins);

      // ── Process each unit ───────────────────────────────────────────────────
      const result = units.map(unit => {
        const { bookings: bks, service: svc, groupKey, isShared, startMins, endMins, totalParty } = unit;
        const primaryBooking = bks[0];

        // Already-assigned asset from first booking (respect existing)
        const existingAssetId = primaryBooking.assetId || primaryBooking.asset_id;
        let chosenAssetId = existingAssetId || '';
        let chosenAsset   = assets.find(a => a.id === chosenAssetId) || null;
        let assetConflict = false;

        // If no asset yet, or existing asset is conflicted, find best one
        if (!chosenAsset || !slotFree(assetTimeline, chosenAssetId, startMins, endMins, groupKey)) {
          if (chosenAsset) assetConflict = true;
          const candidates = assets.filter(a =>
            slotFree(assetTimeline, a.id, startMins, endMins, groupKey) &&
            (a.capacity == null || a.capacity >= totalParty)
          );
          const pick = candidates[0] || null;
          chosenAsset   = pick || chosenAsset; // fall back to original if no free one
          chosenAssetId = chosenAsset?.id || '';
          assetConflict = !pick && !!chosenAsset;
        }

        // Mark asset as used
        if (chosenAssetId) markUsed(assetTimeline, chosenAssetId, startMins, endMins, groupKey);

        // ── Team slots from asset requirements ──────────────────────────────
        const requirements = chosenAsset?.teamRequirements || [];
        const slots = requirements.flatMap(req => {
          const roleIds  = req.roleIds || (req.roleId ? [req.roleId] : []);
          const qty      = req.quantity || 1;
          const roleName = req.roleName || 'Team Member';

          // Exact role match → near-miss fallback → any active
          const exactMatch = teamMembers.filter(tm => roleIds.length === 0 || roleIds.includes(tm.roleId));
          const eligible   = exactMatch.length > 0 ? exactMatch : teamMembers.filter(tm => tm.is_active !== false);
          const isNearMiss = exactMatch.length === 0 && roleIds.length > 0;

          return Array.from({ length: qty }, () => {
            const free   = eligible.filter(tm => slotFree(teamTimeline, tm.id, startMins, endMins, groupKey));
            const pick   = free[0] || eligible[0] || null; // best available; fallback to busy
            const isBusy = pick && !slotFree(teamTimeline, pick.id, startMins, endMins, groupKey);
            if (pick) markUsed(teamTimeline, pick.id, startMins, endMins, groupKey);
            return { roleName, roleIds, suggestedId: pick?.id || '', suggestedName: pick?.name || '',
                     eligibleTeam: eligible, isNearMiss, isBusyFallback: isBusy };
          });
        });

        // ── Build display bookings (one card per source booking for per-ticket)
        return {
          bookings: bks, booking: primaryBooking, // primary for display
          groupKey, isShared, totalParty,
          assetId: chosenAssetId, asset: chosenAsset, assetConflict,
          slots, allAssets: assets, teamMembers,
          label: isShared && bks.length > 1
            ? `${bks.length} tickets · ${svc?.name || primaryBooking.service_name}`
            : null,
        };
      });

      setSuggestions(result);
    } catch (err) {
      console.error('Suggest schedule error:', err);
      toast.error('Failed to generate suggestions');
      setSuggestOpen(false);
    } finally {
      setSuggesting(false);
    }
  };

  const handleConfirmSuggestions = async (notifyTeamFlag, notifyCustomerFlag) => {
    setConfirming(true);
    setNotifyOpen(false);
    try {
      const updates = suggestions.flatMap(s =>
        s.bookings.map(b =>
          orgApi.updateBooking(b.id, {
            assetId: s.assetId || null,
            teamAssignments: s.slots.map(slot => ({
              teamId: slot.suggestedId,
              teamName: slot.suggestedName,
              roleIds: slot.roleIds,
              roleName: slot.roleName,
            })),
            notifyTeam: notifyTeamFlag,
            notifyCustomer: notifyCustomerFlag,
          })
        )
      );
      await Promise.all(updates);
      const notified = [notifyTeamFlag && 'team', notifyCustomerFlag && 'customers'].filter(Boolean).join(' & ');
      toast.success(`Schedule confirmed for ${updates.length} booking${updates.length !== 1 ? 's' : ''}${notified ? ` · ${notified} notified` : ''}!`);
      setSuggestOpen(false);
      mutate();
    } catch (err) {
      console.error('Confirm error:', err);
      toast.error('Failed to save assignments');
    } finally {
      setConfirming(false);
    }
  };

  // Error state
  if (error) {
    return (
      <TabPageLayout title="Schedule" subtitle="Daily booking schedule">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load schedule</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <Button onClick={() => mutate()} className="bg-blue-600 hover:bg-blue-700 text-white h-10">
            Try Again
          </Button>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <>
    <TabPageLayout
      title="Schedule"
      subtitle="Daily booking schedule"
      loading={isLoading}
      actions={
        <div className="flex items-center gap-2">
          {/* Today Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={goToToday}
            className="h-10 px-4 bg-white shadow-[0_8px_24px_rgba(0,0,0,0.04)] hover:bg-gray-50 text-gray-700"
          >
            Today
          </Button>

          {/* Date Navigator */}
          <div className="bg-white rounded-lg flex items-center shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
            <Button variant="ghost" size="sm" onClick={goToPreviousDay} className="h-10 w-10 p-0 rounded-r-none hover:bg-gray-50">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {/* Clickable date — Popover calendar dropdown */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  className="h-10 px-2 sm:px-4 flex items-center gap-1.5 text-sm font-medium text-gray-900 min-w-[110px] sm:min-w-[150px] justify-center cursor-pointer hover:bg-gray-50 rounded transition-colors select-none"
                >
                  <CalendarIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  {formatDateDisplay(currentDate)}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center" sideOffset={4}>
                <MiniCalendar
                  selected={currentDate}
                  onSelect={(date) => {
                    setCurrentDate(date);
                    setTimeout(() => setCalendarOpen(false), 0);
                  }}
                />
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="sm" onClick={goToNextDay} className="h-10 w-10 p-0 rounded-l-none hover:bg-gray-50">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      }
    >
      {/* Contact Modal - Using Dialog for proper focus management */}
      <Dialog open={contactModal.open} onOpenChange={(open) => !open && setContactModal({ open: false, type: null, value: null, name: null, booking: null })}>
        <DialogContent className="w-72 p-0 rounded-2xl border-0 shadow-xl">
          <div className="py-8 px-6">
            <div className="text-center">
              <div className={`w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center ${
                contactModal.type === 'phone' ? 'bg-green-50' : 'bg-blue-50'
              }`}>
                {contactModal.type === 'phone' ? (
                  <Phone className="w-6 h-6 text-green-500" strokeWidth={1.5} />
                ) : (
                  <Mail className="w-6 h-6 text-blue-500" strokeWidth={1.5} />
                )}
              </div>
              <p className="text-sm text-gray-400 font-light mb-1">{contactModal.name}</p>
              <p className="text-lg font-medium text-gray-900 mb-6 select-all tracking-tight">
                {contactModal.value}
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  className={`w-full h-11 text-white font-medium ${
                    contactModal.type === 'phone' 
                      ? 'bg-green-500 hover:bg-green-600' 
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                  onClick={() => {
                    if (contactModal.type === 'phone') {
                      window.location.href = `tel:${contactModal.value}`;
                    } else {
                      window.location.href = `mailto:${contactModal.value}`;
                    }
                  }}
                >
                  {contactModal.type === 'phone' ? 'Call' : 'Send Email'}
                </Button>
                
                {/* Resend Confirmation Button */}
                {contactModal.booking && (
                  <Button
                    variant="outline"
                    className="w-full h-11 font-normal border-gray-200 hover:bg-gray-50"
                    disabled={resendingConfirmation}
                    onClick={() => handleResendConfirmation(contactModal.type === 'phone' ? 'sms' : 'email')}
                  >
                    {resendingConfirmation ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
                        Sending...
                      </span>
                    ) : (
                      `Resend Booking ${contactModal.type === 'phone' ? 'SMS' : 'Email'}`
                    )}
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  className="w-full h-11 text-gray-500 font-normal hover:bg-gray-50"
                  onClick={() => {
                    navigator.clipboard.writeText(contactModal.value);
                    toast.success('Copied to clipboard');
                  }}
                >
                  Copy to Clipboard
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div data-testid="schedule-tab" className="space-y-6">
        {/* Status Filter Pills + Suggest Schedule */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <MultiChipTabs
            value={paymentFilter}
            onChange={setPaymentFilter}
            testIdPrefix="filter"
            removable
            tabs={[
              { value: 'paid', label: 'Paid' },
              { value: 'deposit', label: 'Security Deposit' },
              { value: 'partial', label: 'Partial Paid' },
              { value: 'unpaid', label: 'Unpaid' },
              { value: 'refunded', label: 'Refunded' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
          />
          <SourceFilter enabled={sourceKeys} onToggle={toggleSource} />
          {bookings.length > 0 && (
            <button
              onClick={handleSuggestSchedule}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
              data-testid="suggest-schedule-btn"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Suggest Schedule
            </button>
          )}
        </div>

        {/* Empty State */}
        {!isLoading && bookings.length === 0 ? (
          <div className="space-y-4">
            <EmptyState
              icon={CalendarIcon}
              title="No Bookings"
              description={`No bookings scheduled for ${formatDateDisplay(currentDate)}`}
            />
            {nextBookingDate && (
              <div className="flex justify-center">
                <Button onClick={jumpToNextBooking} className="bg-blue-600 hover:bg-blue-700 text-white h-10 gap-2">
                  Jump to Next Booking
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {/* Summary */}
            <div className="text-sm text-gray-500">
              {bookings.length} booking{bookings.length !== 1 ? 's' : ''} scheduled
            </div>
            
            {/* Hour Groups */}
            {hourSlots.map((hourSlot, slotIndex) => {
              const hourPassed = isHourPassed(hourSlot);
              const slotBookings = groupedBookings[hourSlot];
              const slotColor = SLOT_COLORS[slotIndex % SLOT_COLORS.length];
              
              return (
                <div key={hourSlot} className="space-y-4">
                  {/* Hour Header - Centered text on line */}
                  <div className="flex items-center gap-4">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-sm text-gray-400 font-medium">
                      {hourSlot} · {slotBookings.length} trip{slotBookings.length !== 1 ? 's' : ''}
                    </span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                  
                  {/* Bookings */}
                  <div className="space-y-4">
                    {slotBookings.map((booking) => (
                      <BookingCard
                        key={booking.id}
                        booking={booking}
                        slotColor={slotColor}
                        onContact={handleContact}
                        onEdit={handleEdit}
                        showEditButton={true}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Checkout Bookings: flexible stays checking out today ── */}
        {checkoutBookings.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1 bg-amber-200" />
              <span className="text-xs font-semibold text-amber-600 px-2 py-1 bg-amber-50 rounded-full border border-amber-200 whitespace-nowrap">
                ↩ Checkouts Today · {checkoutBookings.length}
              </span>
              <div className="h-px flex-1 bg-amber-200" />
            </div>
            <div className="space-y-3">
              {checkoutBookings.map((booking) => (
                <div
                  key={`checkout-${booking.id}`}
                  className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3"
                  data-testid={`checkout-card-${booking.confirmation_code}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-600 text-sm">↩</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{booking.customer_name}</div>
                      <div className="text-xs text-gray-500">
                        {booking.service_name}
                        {booking.num_nights ? ` · ${booking.num_nights} nights` : ''}
                        {booking.asset_name ? ` · ${booking.asset_name}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                      Out {booking.start_time}
                    </span>
                    {booking.ticket_url && (
                      <a href={booking.ticket_url} target="_blank" rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline">Ticket</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </TabPageLayout>

      {/* ── Suggest Schedule Modal ─────────────────────────────────────── */}
      <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col p-0 overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-gray-100 flex-shrink-0">
            <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-gray-900 truncate">Suggested Schedule</h2>
              <p className="text-xs text-gray-400">{formatDateDisplay(currentDate)}</p>
            </div>
          </div>
          <DialogTitle className="sr-only">Suggested Schedule</DialogTitle>

          {suggesting ? (
            <div className="flex flex-col items-center py-10 gap-3">
              <Loader2 className="w-7 h-7 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-500">Matching team to bookings…</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-gray-50">
              {suggestions.map(({ booking, bookings: bks, slots, assetId, assetConflict, allAssets, teamMembers, isShared, totalParty, label }, bIdx) => (
                <div key={booking.id} className="p-3 space-y-2">
                  {/* Booking row */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">
                        {(() => {
                          const t = bks[0].start_time?.slice(0,5);
                          if (!t) return '';
                          const [h, m] = t.split(':').map(Number);
                          const ampm = h >= 12 ? 'PM' : 'AM';
                          const h12 = h % 12 || 12;
                          const abbr = bks[0].timezone ? tzAbbr(bks[0].timezone) : '';
                          return `${h12}:${String(m).padStart(2,'0')} ${ampm}${abbr ? ' ' + abbr : ''}`;
                        })()} · {label || bks[0].service_name || bks[0].serviceName}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {isShared && bks.length > 1
                          ? `${bks.map(b => b.customer_name).join(', ')}`
                          : bks[0].customer_name}
                        {' · '}{totalParty}p
                      </p>
                    </div>
                    {/* Asset dropdown */}
                    <div className="relative flex-shrink-0">
                      <select
                        value={assetId}
                        onChange={(e) => {
                          const newAsset = (allAssets || []).find(a => a.id === e.target.value);
                          const reqs = newAsset?.teamRequirements || [];
                          const newSlots = reqs.flatMap(req => {
                            const roleIds = req.roleIds || (req.roleId ? [req.roleId] : []);
                            const qty = req.quantity || 1;
                            const roleName = req.roleName || 'Team Member';
                            const eligible = (teamMembers || []).filter(tm => roleIds.length === 0 || roleIds.includes(tm.roleId));
                            return Array.from({ length: qty }, () => ({
                              roleName, roleIds,
                              suggestedId: eligible[0]?.id || '',
                              suggestedName: eligible[0]?.name || '',
                              eligibleTeam: eligible,
                              isNearMiss: false, isBusyFallback: false
                            }));
                          });
                          setSuggestions(prev => prev.map((s, bi) =>
                            bi !== bIdx ? s : { ...s, assetId: e.target.value, asset: newAsset, slots: newSlots, assetConflict: false }
                          ));
                        }}
                        className={`text-xs text-gray-700 bg-white border rounded-lg pl-2 pr-6 py-1.5 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[110px] ${assetConflict ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}
                      >
                        <option value="">No asset</option>
                        {(allAssets || []).map(a => (
                          <option key={a.id} value={a.id}>{a.name}{a.capacity ? ` · Cap ${a.capacity}` : ''}</option>
                        ))}
                      </select>
                      <Anchor className="w-3 h-3 text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                  {/* Critical warnings — no asset or unresolvable slots */}
                  {!assetId && (
                    <p className="text-xs text-red-600 bg-red-50 px-2 py-1.5 rounded-lg flex items-center gap-1.5">
                      <span className="font-semibold">!</span> No asset available for this time slot — assign one manually
                    </p>
                  )}
                  {slots.some(s => !s.suggestedId) && (
                    <p className="text-xs text-red-600 bg-red-50 px-2 py-1.5 rounded-lg flex items-center gap-1.5">
                      <span className="font-semibold">!</span> {slots.filter(s => !s.suggestedId).length} role{slots.filter(s => !s.suggestedId).length > 1 ? 's' : ''} unassigned — no team member with the required role
                    </p>
                  )}
                  {assetConflict && (
                    <p className="text-[10px] text-orange-600 bg-orange-50 px-2 py-1 rounded">
                      Asset conflict — original boat is already in use. Reassigned to next available.
                    </p>
                  )}
                  {isShared && bks.length > 1 && (
                    <p className="text-[10px] text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      Per-ticket: {bks.length} bookings share this asset and team ({totalParty}p total)
                    </p>
                  )}

                  {/* Team slots */}
                  {slots.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No team requirements — pick an asset with roles</p>
                  ) : slots.map((slot, sIdx) => (
                    <div key={sIdx} className="flex items-center gap-1.5">
                      <Users className="w-3 h-3 text-gray-300 flex-shrink-0" />
                      <span className="text-xs text-gray-500 flex-shrink-0 w-[70px] truncate" title={slot.roleName}>{slot.roleName}</span>
                      {slot.isNearMiss && <span className="text-[9px] text-amber-600 bg-amber-50 px-1 rounded-full flex-shrink-0">~</span>}
                      {slot.isBusyFallback && <span className="text-[9px] text-orange-600 bg-orange-50 px-1 rounded-full flex-shrink-0">!</span>}
                      <div className="relative flex-1 min-w-0">
                        <select
                          value={slot.suggestedId}
                          onChange={(e) => {
                            const tm = slot.eligibleTeam.find(t => t.id === e.target.value);
                            setSuggestions(prev => prev.map((s, bi) =>
                              bi !== bIdx ? s : {
                                ...s,
                                slots: s.slots.map((sl, si) =>
                                  si !== sIdx ? sl : { ...sl, suggestedId: e.target.value, suggestedName: tm?.name || '' }
                                )
                              }
                            ));
                          }}
                          className={`text-xs font-medium w-full bg-white border rounded-lg pl-2 pr-5 py-1.5 appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                            slot.suggestedId ? 'text-gray-900 border-gray-200' : 'text-red-400 border-red-200'
                          }`}
                        >
                          <option value="">— Unassigned</option>
                          {slot.eligibleTeam.map(tm => (
                            <option key={tm.id} value={tm.id}>{tm.name}</option>
                          ))}
                        </select>
                        <ChevronDown className="w-3 h-3 text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              {suggestions.length === 0 && !suggesting && (
                <div className="text-center py-8 text-sm text-gray-400">No bookings with team requirements for this day.</div>
              )}
            </div>
          )}

          {!suggesting && suggestions.length > 0 && (
            <div className="flex gap-2 px-3 py-3 border-t border-gray-100 bg-white flex-shrink-0 sticky bottom-0">
              <Button variant="outline" className="flex-1 h-9 text-sm" onClick={() => setSuggestOpen(false)}>Cancel</Button>
              <Button className="flex-1 h-9 text-sm bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setNotifyOpen(true)} disabled={confirming}>
                {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                {confirming ? 'Saving…' : <><span className="hidden sm:inline">Confirm Schedule</span><span className="sm:hidden">Confirm</span></>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Notify Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
            Send notifications?
          </DialogTitle>
          <p className="text-xs text-gray-500 -mt-1">Choose who to notify about their assignment.</p>
          <div className="space-y-2 my-1">
            <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
              <input type="checkbox" checked={notifyTeam} onChange={e => setNotifyTeam(e.target.checked)} className="w-4 h-4 accent-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Notify team members</p>
                <p className="text-xs text-gray-500 mt-0.5">SMS/email their assignment for today</p>
              </div>
              <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </label>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
              <input type="checkbox" checked={notifyCustomer} onChange={e => setNotifyCustomer(e.target.checked)} className="w-4 h-4 accent-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Notify customers</p>
                <p className="text-xs text-gray-500 mt-0.5">Email booking confirmation to all {suggestions.flatMap(s => s.bookings).length} customer{suggestions.flatMap(s => s.bookings).length !== 1 ? 's' : ''}</p>
              </div>
              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1 h-9 text-sm" onClick={() => handleConfirmSuggestions(false, false)}>
              Save only
            </Button>
            <Button className="flex-1 h-9 text-sm bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleConfirmSuggestions(notifyTeam, notifyCustomer)} disabled={confirming}>
              {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save & Notify'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {editing && (
        <EditBookingModal
          isOpen={true}
          trip={editing}
          assets={editCtx.assets}
          teamMembers={editCtx.teamMembers}
          roles={editCtx.roles}
          services={editCtx.services}
          allBookings={bookings}
          onClose={() => {
            setEditing(null);
            mutate();
          }}
          onSave={async (bookingId, bookingData) => {
            try {
              const cleanId = (v) => (!v || v === 'unassigned') ? null : v;
              await orgApi.updateBooking(bookingId, {
                bookingDate: bookingData.date,
                startTime: bookingData.startTime,
                durationMinutes: bookingData.duration,
                internalNotes: bookingData.notes || '',
                teamAssignments: (bookingData.teamAssignments || []).map((a) => ({ ...a, teamId: cleanId(a.teamId) || '' })),
                customerName: bookingData.customerName,
                customerEmail: bookingData.customerEmail,
                customerPhone: bookingData.customerPhone,
                pickupLocation: bookingData.pickupLocation,
                paymentStatus: bookingData.paymentStatus,
                checkedIn: bookingData.checkedIn,
                timezone: bookingData.timezone,
                assetId: cleanId(bookingData.assetId),
                partySize: bookingData.partySize,
                emergencyContactNumber: bookingData.emergencyContactNumber,
                selectedAddOns: bookingData.selectedAddOns,
                city: bookingData.city,
                country: bookingData.country,
                notifyTeam: bookingData.notifyTeam ?? false,
                notifyCustomer: bookingData.notifyCustomer ?? false,
              });
              setEditing(null);
              toast.success('Booking updated successfully!');
              await new Promise((r) => setTimeout(r, 300));
              mutate();
            } catch (error) {
              const d = error.response?.data?.detail;
              if (typeof d === 'object' && d?.message) toast.error(d.message);
              else if (typeof d === 'string') toast.error(d);
              else toast.error('Failed to update booking');
            }
          }}
        />
      )}
    </>
  );
};

export default ScheduleTab;
